import './styles.scss'

import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Form, Input, Modal, Upload } from 'antd'
import type { FormInstance } from 'antd'

import { PlusIcon } from '@/components/Icons'
import { ACCEPTED_FILE_EXTENSIONS } from '@/libs/constants/upload'

const acceptedFileExtensions = Object.values(
  ACCEPTED_FILE_EXTENSIONS.IMAGES,
).join(', ')

interface UploadDamagedPictureProps {
  form?: FormInstance
}

const UploadDamagedPicture = ({ form }: UploadDamagedPictureProps) => {
  const MAX_TOTAL_SIZE = 20

  const [previewVisible, setPreviewVisible] = useState(false)
  const [previewImage, setPreviewImage] = useState('')
  const [previewTitle, setPreviewTitle] = useState('')
  const [currentFileList, setCurrentFileList] = useState([])
  const [uploadFileErrorMessage, setUploadFileErrorMessage] = useState(null)
  const { t } = useTranslation()

  const convertFromByteToMb = useCallback((number: number) => {
    return number / 1024 ** 2
  }, [])

  const totalFileSize = useMemo(() => {
    const result = currentFileList.reduce(
      (total, file) => total + convertFromByteToMb(file.size),
      0,
    )
    return result
  }, [currentFileList, convertFromByteToMb])

  const uploadButton = (
    <div className="upload-block__button">
      <PlusIcon />
      <div>{t('service.upload.button')}</div>
    </div>
  )

  const handleCancel = () => setPreviewVisible(false)

  const getBase64 = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.addEventListener('load', () => resolve(reader.result))
      reader.onerror = (error) => reject(error)
    })
  }

  const handlePreview = async (file) => {
    setPreviewImage(file.url || file.imageBase64)
    setPreviewVisible(true)
    setPreviewTitle(
      file.name || file.url.slice(Math.max(0, file.url.lastIndexOf('/') + 1)),
    )
  }

  const handleValidateFilesSize = async (fileList) => {
    const mappedFile = fileList.map(async (file) => {
      let imageBase64 = null
      if (!file.url && !file.imageBase64) {
        imageBase64 = await getBase64(file.originFileObj)
      }

      if (totalFileSize + convertFromByteToMb(file.size) > MAX_TOTAL_SIZE) {
        setUploadFileErrorMessage(t('service.upload.totalSizeLessThan20'))
        return {
          error: true,
          ...file,
        }
      }
      setUploadFileErrorMessage(null)
      const temporaryFile = {
        fileName: file.name,
        imageBase64: file.imageBase64 || imageBase64,
        error: false,
        ...file,
      }
      return temporaryFile
    })
    return Promise.all(mappedFile)
  }

  const handleChange = async ({ fileList }) => {
    let newFileList = await handleValidateFilesSize(fileList)
    newFileList = newFileList.filter((file) => !file.error)
    setCurrentFileList(newFileList)
    form.setFieldsValue({
      damagedImages: newFileList,
    })
  }

  const handleBeforeUpload = () => {
    return false
  }

  return (
    <div className="upload-block__wrapper service-request__layout--shrink">
      <div className="upload-block__title">
        {t('service.selectService.addDamagedPictures')}
      </div>
      <Upload
        accept={acceptedFileExtensions}
        beforeUpload={handleBeforeUpload}
        className="upload-block__content"
        fileList={currentFileList}
        listType="picture-card"
        onChange={handleChange}
        onPreview={handlePreview}
      >
        {uploadButton}
      </Upload>
      <p className="upload-file__error">{uploadFileErrorMessage}</p>
      <Modal
        footer={null}
        open={previewVisible}
        title={previewTitle}
        onCancel={handleCancel}
      >
        <img
          alt="example"
          loading="lazy"
          src={previewImage}
          style={{ width: '100%' }}
        />
      </Modal>
      <Form.Item hidden form={form} initialValue={[]} name="damagedImages">
        <Input
          className="delivery-content__field-input--disabled"
          data-testid="partner-number"
        />
      </Form.Item>
    </div>
  )
}

export default UploadDamagedPicture
